/**
 * Composant Sidebar - Barre de navigation verticale pour l'interface d'administration
 * 
 * Ce composant affiche une barre de navigation fixe à gauche de l'écran avec:
 * - Logo/nom de l'application
 * - Navigation principale avec icônes
 * - Section configuration
 * - Bouton de déconnexion
 * - Design responsive avec menu hamburger pour mobile
 * 
 * Structure:
 * - État pour gérer l'ouverture du menu mobile
 * - Styles CSS-in-JS pour le design responsive
 * - Navigation avec React Router NavLink
 * - Gestion de la déconnexion via authStore
 */

import React, { useState, useCallback, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../features/auth/hooks/useAuth";
import { toast } from "../services/apiClient";

export default function Sidebar() {
  // État pour contrôler l'ouverture du menu mobile (responsive)
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Hooks React Router pour la navigation et la localisation
  const loc = useLocation();
  const navigate = useNavigate();
  
  // Hook authStore pour la déconnexion
  const { logout } = useAuthStore();

  /**
   * Ferme automatiquement le menu mobile lors de la navigation
   * S'exécute à chaque changement de pathname
   */
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  /**
   * Gère la déconnexion de l'utilisateur
   * - Déconnecte via authStore
   * - Affiche un toast de confirmation
   * - Redirige vers la page de login
   */
  const handleLogout = useCallback(() => {
    logout();
    toast.success("Deconnecte.");
    navigate("/login", { replace: true, state: { from: loc } });
  }, [logout, navigate, loc]);

  return (
    <>
      {/* Styles CSS-in-JS pour le design de la sidebar */}
      <style>{`
        /* Container principal de la sidebar */
        .sb-container {
          width: 240px;
          height: 100vh;
          max-height: 100vh;
          background: var(--brand-xd, #352394);
          display: flex;
          flex-direction: column;
          position: fixed;
          left: 0;
          top: 0;
          z-index: 1000;
          box-shadow: 2px 0 8px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
          overflow: hidden;
        }
        /* Overlay sombre pour mobile quand menu ouvert */
        .sb-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 999;
          display: none;
        }
        .sb-overlay.open {
          display: block;
        }
        /* Header mobile avec hamburger (visible uniquement sur mobile) */
        .sb-mobile-header {
          display: none;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--brand-xd, #352394);
          color: white;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 998;
          height: 50px;
        }
        /* Brand/logo de l'application */
        .sb-brand {
          padding: 20px;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          flex-shrink: 0;
        }
        .sb-brand strong {
          color: white;
          font-size: 18px;
          font-weight: 700;
        }
        /* Navigation principale */
        .sb-nav {
          flex: 1;
          padding: 12px;
          overflow-y: auto;
          overflow-x: hidden;
        }
        /* Section de navigation */
        .sb-section {
          margin-bottom: 20px;
        }
        /* Titre de section */
        .sb-section-title {
          color: rgba(255,255,255,0.6);
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 8px 12px;
          margin-bottom: 4px;
        }
        /* Lien de navigation */
        .sb-link {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          color: rgba(255,255,255,0.8);
          text-decoration: none;
          border-radius: 6px;
          margin-bottom: 2px;
          font-size: 14px;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .sb-link svg {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }
        .sb-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .sb-link.active {
          background: var(--accent, #F2E71C);
          color: var(--brand-xd, #352394);
          font-weight: 600;
        }
        /* Footer avec bouton déconnexion */
        .sb-footer {
          padding: 12px;
          border-top: 1px solid rgba(255,255,255,0.1);
          flex-shrink: 0;
        }
        .sb-logout {
          width: 100%;
          padding: 10px 12px;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          color: white;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }
        .sb-logout:hover {
          background: rgba(255,255,255,0.2);
        }
        /* Bouton hamburger mobile */
        .sb-hamburger {
          display: none;
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 8px;
        }
        .sb-hamburger svg {
          width: 24px;
          height: 24px;
        }
        /* Zone de contenu principal */
        .sb-main {
          margin-left: 240px;
          min-height: 100vh;
          padding: 24px;
          box-sizing: border-box;
        }
        /* Responsive: mobile */
        @media (max-width: 960px) {
          .sb-container {
            transform: translateX(-100%);
          }
          .sb-container.open {
            transform: translateX(0);
          }
          .sb-mobile-header {
            display: flex;
          }
          .sb-hamburger {
            display: block;
          }
          .sb-main {
            margin-left: 0;
            padding-top: 74px;
            padding-left: 24px;
            padding-right: 24px;
          }
        }
      `}</style>

      {/* Header mobile avec hamburger (visible uniquement sur mobile) */}
      <div className="sb-mobile-header">
        <strong>Rema admin 2026</strong>
        <button
          className="sb-hamburger"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {mobileOpen ? (
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
            ) : (
              <path d="M3 12h18M3 6h18M3 18h18" strokeLinecap="round" strokeLinejoin="round"/>
            )}
          </svg>
        </button>
      </div>

      {/* Overlay sombre pour mobile (ferme le menu au clic) */}
      <div 
        className={`sb-overlay ${mobileOpen ? 'open' : ''}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Sidebar principale */}
      <aside className={`sb-container ${mobileOpen ? 'open' : ''}`}>
        {/* Brand/logo */}
        <div className="sb-brand">
          <strong>Rema admin 2026</strong>
        </div>

        {/* Navigation */}
        <nav className="sb-nav">
          {/* Section: Gestion du contenu */}
          <div className="sb-section">
            <div className="sb-section-title">Gestion du contenu</div>
            
            {/* Lien Programme */}
            <NavLink
              to="/programme"
              className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Programme
            </NavLink>
            
            {/* Lien Infos pratiques */}
            <NavLink
              to="/infos"
              className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              Infos pratiques
            </NavLink>
            
            {/* Lien Personnes */}
            <NavLink
              to="/people"
              className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Personnes
            </NavLink>
          </div>

          {/* Section: Configuration */}
          <div className="sb-section">
            <div className="sb-section-title">Configuration</div>
            
            <NavLink
              to="/settings/days"
              className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              Réglages jours
            </NavLink>
            <NavLink
              to="/settings/partners-banner"
              className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Partenaires
            </NavLink>
            <NavLink
              to="/settings/social-links"
              className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
              Réseaux sociaux
            </NavLink>
            <NavLink
              to="/settings/locations"
              className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              Lieux
            </NavLink>
            <NavLink
              to="/settings/info-categories"
              className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
              Catégories
            </NavLink>
          </div>
        </nav>

        {/* Footer avec bouton déconnexion */}
        <div className="sb-footer">
          <button
            type="button"
            className="sb-logout"
            onClick={handleLogout}
          >
            Se déconnecter
          </button>
        </div>
      </aside>
    </>
  );
}
